import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearFormDetailsReducer } from "@/reducers/form";
import {
  createForm,
  editFormNames,
  getFormDetails,
  getUserRoleList,
  uploadMediaImage,
  uploadFile,
} from "@/services";
import { usePageTitle } from "@/helpers";
import { buildStatePatchFromFormDetails } from "./addFormDetailsMapper";
import { buildFormSubmitPayload } from "./addFormSubmitPayload";
import {
  generateRandomNumber,
  removeElement,
  findElementAndParent,
} from "./addFormUtils";
import { isValidEmail } from "@/helpers/validations";

export default function useAddForm() {
  const formData = useSelector((state) => {
    return {
      allRoleList: state.people.allRoleList,
      getFormDetailsData: state.form.getFormDetailsData,
      parentCategories: state.equipment.parentCategories,
      currentUser: state.auth.user,
    };
  });
  const { getFormDetailsData, parentCategories, allRoleList, currentUser } =
    formData;
  const userRole =
    allRoleList?.filter((item) => {
      return item?.level >= currentUser?.user_role?.level;
    }) || [];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({});
  const {
    editID,
    status,
    name = "",
    emails = [],
    isEmailInvalid = false,
    alreadyExistsEmail = false,
    is_public,
    accessLevel,
    statusField,
    keyName = "",
    isEdit = true,
    savedData = [],
    order = "",
    minValue = "",
    maxValue = "",
    prefixValue = "",
    suffixValue = "",
    formElementType,
    required = false,
    description = "",
    submit_message = "",
    placeholder = "",
    formModal = false,
    optionsData = [],
    rowOptionData = [],
    columnOptionData = [],
    parentValue = "",
    parentId = "",
    isNewFormModal = false,
    isCheckParentValue = false,
    otherDataValueOptions = [],
    otherDisplayValueOptions = [],
    informationEditorContent = "",
    informationModalDisplay = "d-block",
    email_displayed = true,
    email_required = false,
    responseNotification = false,
  } = state;
  const [loading, setLoading] = useState(true);
  const [submitMessageLoading, setSubmitMessageLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const formIds = params.get("form-id");
  const informationRef = useRef(null);
  const labelNameRef = useRef(null);
  const submitMessageRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (formIds) {
      dispatch(getFormDetails(formIds)).then((res) => {
        if (res?.data && res?.data?.payload) {
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    } else {
      dispatch(clearFormDetailsReducer());
      setLoading(false);
    }
    dispatch(getUserRoleList());
  }, [formIds, dispatch]);

  usePageTitle(formIds ? "Edit Form" : "Create Form");

  const isMultipleChoiceGrid = ["multiple_choice_grid"]?.includes(
    state.formElementType?.toLowerCase(),
  );
  const isLinearOption = ["linear"]?.includes(
    state.formElementType?.toLowerCase(),
  );
  const isCheckBoxRadioOptions = ["checkbox", "radio"]?.includes(
    state.formElementType?.toLowerCase(),
  );
  const isSelectOption = ["select"]?.includes(
    state.formElementType?.toLowerCase(),
  );
  const isMultiOptions = ["checkbox", "radio", "select"]?.includes(
    state.formElementType?.toLowerCase(),
  );
  const isOther = ["scale"]?.includes(state.formElementType?.toLowerCase());
  const isInformation = ["information"]?.includes(
    state.formElementType?.toLowerCase(),
  );

  const isPrivacyPolicy =
    is_public === "Shared" ? is_public && accessLevel : is_public;
  const isDisabled =
    !name ||
    !status ||
    !isPrivacyPolicy ||
    (responseNotification && !emails?.length) ||
    isEmailInvalid ||
    alreadyExistsEmail;

  const switchBtnColor = (active) => {
    return active ? "Bg-success" : "Bg-danger";
  };

  const isDisabledFormField =
    !keyName ||
    !statusField ||
    !formElementType ||
    !order ||
    (isOther &&
      (!otherDisplayValueOptions?.length || !otherDataValueOptions?.length)) ||
    (!isNewFormModal && isCheckParentValue && !parentValue?.length) ||
    (isMultiOptions
      ? !optionsData?.length
      : isMultipleChoiceGrid
        ? !rowOptionData?.length || !columnOptionData?.length
        : null) ||
    (isLinearOption &&
      (!minValue || !maxValue || !prefixValue || !suffixValue));

  useEffect(() => {
    if (!formModal) {
      setState((prev) => ({
        ...prev,
        keyName: "",
        required: "",
        labelName: "",
        placeholder: "",
        minValue: "",
        maxValue: "",
        prefixValue: "",
        suffixValue: "",
        order: "",
        statusField: "",
        formElementType: "",
        parentValue: "",
        optionsData: [],
        rowOptionData: [],
        columnOptionData: [],
        otherDataValueOptions: [],
        otherDisplayValueOptions: [],
        options: "",
        columnOption: "",
        rowOption: "",
        errorMessage: "",
        errorMessageRow: "",
        errorMessageColumn: "",
        scaleDataError: "",
        informationEditorContent: "",
      }));
    }
  }, [formModal]);

  useEffect(() => {
    if (!getFormDetailsData) return;
    setState((prev) => ({
      ...prev,
      ...buildStatePatchFromFormDetails(getFormDetailsData),
    }));
  }, [getFormDetailsData]);

  useEffect(() => {
    if (savedData?.length) {
      const val = [...savedData].sort((a, b) => a?.order - b?.order);

      setState((prev) => ({
        ...prev,
        savedData: val,
      }));
    }
  }, [savedData, formModal]);

  const guardRedirect = !parentCategories?.includes("form");

  const checkAndSetErrorMessage = (errorMessageKey, message) => {
    setState((prev) => ({ ...prev, [errorMessageKey]: message }));
  };

  const handleOnSubmitOptions = (valueKey, dataKey, errorMessageKey) => {
    const convertedArray = state?.[dataKey]?.map((item) =>
      item?.trim().toLowerCase(),
    );
    if (!state?.[valueKey]) {
      checkAndSetErrorMessage(errorMessageKey, "Please Enter a value");
    } else if (
      convertedArray.includes(state?.[valueKey].trim().toLowerCase())
    ) {
      checkAndSetErrorMessage(
        errorMessageKey,
        "This Options field is already added.",
      );
    } else {
      setState((prev) => ({
        ...prev,
        [valueKey]: "",
        [errorMessageKey]: "",
        [dataKey]: [...(prev?.[dataKey] || []), prev?.[valueKey]],
      }));
    }
  };

  const handleOnPressCancel = (item, key) => {
    const filterOptionsData = state?.[key]?.filter(
      (filterItem) => filterItem !== item,
    );
    setState((prev) => ({
      ...prev,
      [key]: filterOptionsData,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value.trimStart();

    if (["order"].includes(name)) {
      processedValue = processedValue.replace(/\D/g, "");
    }

    setState((prev) => ({
      ...prev,
      [name]: processedValue,
      errorMessage: "",
      errorMessageRow: "",
      errorMessageColumn: "",
    }));
  };

  const responseFunction = (apiCall, isEditRoute) => {
    setIsSubmitLoading(true);
    apiCall
      .unwrap()
      .then((res) => {
        if (res?.success) {
          navigate("/forms");
          toast.success(res?.message);
          setTimeout(() => setIsSubmitLoading(false), 100);
        } else {
          setIsSubmitLoading(false);
        }
      })
      .catch((error) => {
        toast.error(error?.message || "Something went wrong!");
        setIsSubmitLoading(false);
      });
  };

  const handleSubmitForm = (event) => {
    event?.preventDefault();
    if (isSubmitLoading) return;

    const payload = buildFormSubmitPayload({
      name,
      responseNotification,
      emails,
      formIds,
      description,
      status,
      is_public,
      accessLevel,
      submitMessageRef,
      email_displayed,
      email_required,
      savedData,
    });
    if (!getFormDetailsData?.id && !formIds) {
      responseFunction(dispatch(createForm(payload)), false);
    } else {
      responseFunction(
        dispatch(editFormNames({ id: getFormDetailsData?.id, data: payload })),
        true,
      );
    }
  };

  const handleFormModal = () => {
    setState((prev) => ({
      ...prev,
      formModal: true,
      childFormModal: false,
      parentId: "",
      isEdit: false,
      isNewFormModal: true,
    }));
  };

  const testId = !formIds ? "addForm" : "editForm";

  const handleChildSave = (oldData, childData) => {
    const newData = oldData.map((item) => {
      if (item.id === childData.parentId) {
        let newItem = { ...item };
        newItem["elements"] = newItem.elements
          ? [...newItem.elements, childData]
          : [childData];
        return newItem;
      } else {
        if (item?.elements?.length > 0) {
          const updatedElements = handleChildSave(item.elements, childData);
          return { ...item, elements: updatedElements };
        } else {
          return item;
        }
      }
    });
    return newData;
  };

  const checkBoxOptions = state.optionsData?.map((item) => {
    return {
      value: item?.toLowerCase(),
      label: item,
    };
  });

  const editElement = (elements, editedElement) => {
    return elements.map((item) => {
      if (item.id === editedElement.id) {
        return {
          ...item,
          id: item.id,
          keyName: editedElement.keyName,
          required: editedElement.required,
          labelName: editedElement.labelName,
          order: editedElement.order,
          placeholder: editedElement.placeholder,
          statusField: editedElement.statusField?.toLowerCase(),
          parentValue: parentValue,
          optionsData: editedElement.optionsData,
          rowOptionData: editedElement.rowOptionData,
          columnOptionData: editedElement.columnOptionData,
          formElementType: editedElement.formElementType?.toLowerCase(),
          otherDisplayValueOptions: editedElement.otherDisplayValueOptions,
          otherDataValueOptions: editedElement.otherDataValueOptions,
          ...(isLinearOption
            ? {
                options: {
                  min: minValue,
                  max: maxValue,
                  prefix: prefixValue,
                  suffix: suffixValue,
                },
              }
            : {}),
          ...(isMultipleChoiceGrid
            ? {
                options: {
                  rows: editedElement.rowOptionData,
                  columns: editedElement.columnOptionData,
                },
              }
            : {}),
          ...(isCheckBoxRadioOptions
            ? { options: editedElement.optionsData }
            : {}),
          ...(isSelectOption ? { options: checkBoxOptions } : {}),
          ...(isOther
            ? {
                options: {
                  display_value: editedElement.otherDisplayValueOptions,
                  data_value: editedElement.otherDataValueOptions,
                },
              }
            : {}),
          ...(isInformation
            ? { options: informationRef?.current?.getContent() }
            : {}),
        };
      } else if (item.elements) {
        return {
          ...item,
          elements: editElement(item.elements, editedElement),
        };
      }
      return item;
    });
  };

  const hideShowInformationModal = (value) => {
    setState((prev) => ({ ...prev, informationModalDisplay: value }));
  };

  const handleFormField = () => {
    if (
      formElementType &&
      ["select", "checkbox", "radio"].includes(formElementType) &&
      optionsData?.length < 2
    ) {
      toast.error("Add at least 2 options!");
    } else if (!labelNameRef?.current?.getContent()) {
      toast.error("Please Enter Label Name");
    } else {
      const isValidateRequired = isInformation ? false : required;
      if (!isEdit) {
        let data = {
          keyName: keyName,
          required: isValidateRequired,
          labelName: labelNameRef?.current?.getContent(),
          order: order,
          placeholder: placeholder,
          id: generateRandomNumber(),
          formElementType: formElementType.toLowerCase() || "",
          parentValue: parentValue,
          parentId: parentId,
          statusField: statusField.toLowerCase() || "",
          optionsData: optionsData,
          rowOptionData: rowOptionData,
          columnOptionData: columnOptionData,
          otherDisplayValueOptions: otherDisplayValueOptions,
          otherDataValueOptions: otherDataValueOptions,
          ...(isLinearOption
            ? {
                options: {
                  min: minValue,
                  max: maxValue,
                  prefix: prefixValue,
                  suffix: suffixValue,
                },
              }
            : {}),
          ...(isMultipleChoiceGrid
            ? {
                options: {
                  rows: rowOptionData,
                  columns: columnOptionData,
                },
              }
            : {}),
          ...(isOther
            ? {
                options: {
                  display_value: otherDisplayValueOptions,
                  data_value: otherDataValueOptions,
                },
              }
            : {}),
          ...(isCheckBoxRadioOptions ? { options: optionsData } : {}),
          ...(isSelectOption ? { options: checkBoxOptions } : {}),
          ...(isInformation
            ? { options: informationRef?.current?.getContent() }
            : {}),
          ...(state.childFormModal ? { newlyCreated: true } : {}),
          parent: true,
          ...(state?.childFormModal
            ? { parent_id: generateRandomNumber() }
            : {}),
        };
        if (state?.childFormModal) {
          const childData = handleChildSave(savedData, data);
          setState((prev) => ({
            ...prev,
            formModal: false,
            savedData: childData,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            formModal: false,
            savedData: [...savedData, data],
          }));
        }
      } else {
        if (isEdit) {
          const editData = editElement(savedData, {
            id: editID,
            keyName: keyName,
            required: isValidateRequired,
            labelName: labelNameRef?.current?.getContent(),
            order: order,
            placeholder: placeholder,
            optionsData: optionsData,
            parentValue: parentValue,
            statusField: statusField.toLowerCase() || "",
            formElementType: formElementType?.toLowerCase() || "",
            rowOptionData: rowOptionData,
            columnOptionData: columnOptionData,
            otherDisplayValueOptions: otherDisplayValueOptions,
            otherDataValueOptions: otherDataValueOptions,
            informationEditorContent: informationRef?.current?.getContent(),
          });
          setState((prev) => ({
            ...prev,
            formModal: false,
            savedData: editData,
          }));
        }
      }
    }
  };

  const handleFormFieldDelete = (item) => {
    const updatedFormFieldsList = removeElement(savedData, item?.id);
    setState((prev) => ({ ...prev, savedData: updatedFormFieldsList }));
  };

  const handleFormFieldUpdate = (item) => {
    const isParent =
      (item.hasOwnProperty("parentId") && item.parentId !== "") ||
      (item.hasOwnProperty("parent_value") && item?.parent_value !== "");
    if (item?.parent_value) {
      const { parent } = findElementAndParent(savedData, item?.id);
      setState((prev) => ({
        ...prev,
        parentValueData: parent?.optionsData,
      }));
    }
    const checkIsArray = Array.isArray(item?.optionsData);
    setState((prev) => ({
      ...prev,
      isEdit: true,
      formModal: true,
      editID: item?.id,
      keyName: item?.keyName,
      required: item?.required,
      labelName: item?.labelName,
      order: item?.order,
      minValue: item?.options?.min,
      maxValue: item?.options?.max,
      prefixValue: item?.options?.prefix,
      suffixValue: item?.options?.suffix,
      placeholder: item?.placeholder,
      formElementType: item?.formElementType || "",
      ...(checkIsArray ? { optionsData: item?.optionsData } : {}),
      parentValue: item?.parentValue || item?.parent_value,
      statusField: item?.statusField.toLowerCase() || "",
      rowOptionData: item?.rowOptionData,
      columnOptionData: item?.columnOptionData,
      isNewFormModal: false,
      isCheckParentValue: isParent,
      otherDisplayValueOptions: item?.otherDisplayValueOptions,
      otherDataValueOptions: item?.otherDataValueOptions,
      informationEditorContent: item?.options,
    }));
  };

  const handleOnChangeSelect = (e) => {
    const { name, id } = e.target;
    const selectValue = JSON.parse(id);

    setState((prev) => ({
      ...prev,
      ...(name === "is_public" && { accessLevel: "" }),
      [name]: name === "accessLevel" ? selectValue.id : selectValue.name,
    }));
  };

  const handleChildFormModal = (item) => {
    setState((prev) => ({
      ...prev,
      formModal: true,
      childFormModal: true,
      parentValueData: item?.optionsData,
      parentId: item?.id,
      isEdit: false,
      isNewFormModal: false,
      isCheckParentValue: true,
    }));
  };

  const handleDragEndData = (key, item, otherDataKey, otherItemData) => {
    setState((prev) => ({ ...prev, [key]: item }));
    if (otherDataKey) {
      setState((prev) => ({ ...prev, otherDataValueOptions: otherItemData }));
    }
  };

  const handleDragEndDataForSavedData = (item) => {
    if (item) {
      const changeOrder = item?.map((item, index) => ({
        ...item,
        order: index + 1,
      }));
      setState((prev) => ({ ...prev, savedData: changeOrder }));
    }
  };

  const handleEditorInit = (editor, editRef) => {
    submitMessageRef.current = editRef;
  };

  const setDataEditor = (url, cb, type) => {
    if (type?.type === "audio/mpeg") {
      var audioHtml = "<audio controls>";
      audioHtml += '<source src="' + url + '" type="' + type.type + '">';
      audioHtml += "Your browser does not support the audio element.";
      audioHtml += "</audio>";
      window.tinymce.activeEditor.insertContent(audioHtml);
      window.tinymce.activeEditor.windowManager.close();
    } else {
      cb(url, {
        title: type.name,
      });
    }
  };

  const handleFileChange = async (e, cb) => {
    const file = e?.[0];
    if (!file) return;
    if (
      file.type === "image/heic" ||
      file.name.toLowerCase().endsWith(".heic")
    ) {
      toast.info(
        "HEIC files are not supported. Please select a different file.",
      );
      return;
    }
    if (file.size > 209715200) {
      toast.info("File size should be less than 200MB");
      return;
    }
    setSubmitMessageLoading(true);
    const imageTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    const isImage = imageTypes.includes(file.type);
    const data = new FormData();
    if (isImage) {
      data.append("image", file, file.name);
    } else {
      data.append("file", file, file.name);
    }
    try {
      let res;
      if (isImage) {
        res = await dispatch(uploadMediaImage({ filess: data })).unwrap();
      } else {
        res = await dispatch(uploadFile(data)).unwrap();
      }
      if (res.url) {
        setSubmitMessageLoading(false);
        setDataEditor(res.url, cb, file);
      } else {
        toast.info("File content too large.");
      }
    } catch (error) {
      setSubmitMessageLoading(false);
      toast.error("File upload failed.");
    }
  };

  const handleEmailDisplayed = () => {
    setState((prev) => {
      const newEmailDisplayed = !prev.email_displayed;
      return {
        ...prev,
        email_displayed: newEmailDisplayed,
        email_required: newEmailDisplayed ? prev.email_required : false,
      };
    });
  };

  const handleEmailRequired = () => {
    setState((prev) => {
      const newEmailRequired = !prev.email_required;
      return {
        ...prev,
        email_required: newEmailRequired,
        email_displayed: newEmailRequired ? true : prev.email_displayed,
      };
    });
  };

  const handleResponseNotification = () => {
    setState((prev) => ({
      ...prev,
      responseNotification: !prev.responseNotification,
      isEmailInvalid: false,
      alreadyExistsEmail: false,
    }));
  };

  const handleInputChange = (event) => {
    const inputValue = event.target.value.trim();
    setState((prev) => ({
      ...prev,
      isEmailInvalid: false,
      alreadyExistsEmail: false,
    }));
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      event.preventDefault();
      if (inputValue && isValidEmail(inputValue)) {
        addEmail(inputValue);
      } else {
        setState((prev) => ({
          ...prev,
          isEmailInvalid: true,
          alreadyExistsEmail: false,
        }));
        inputRef.current.value = "";
      }
    }
  };

  const addEmail = (email) => {
    const lowercaseEmail = email.toLowerCase();
    if (!emails.includes(lowercaseEmail) && emails.length < 20) {
      setState((prev) => ({
        ...prev,
        isEmailInvalid: false,
        alreadyExistsEmail: false,
        emails: [...emails, lowercaseEmail],
      }));
      inputRef.current.value = "";
    } else {
      setState((prev) => ({
        ...prev,
        isEmailInvalid: false,
        alreadyExistsEmail: true,
      }));
    }
  };

  const removeEmail = (index) => {
    setState((prev) => ({
      ...prev,
      emails: prev?.emails.filter((_, i) => i !== index),
    }));
  };

  const closeFormModal = () =>
    setState((prev) => ({ ...prev, formModal: false }));

  return {
    guardRedirect,
    loading,
    formIds,
    navigate,
    testId,
    state,
    setState,
    userRole,
    name,
    status,
    is_public,
    accessLevel,
    description,
    email_displayed,
    email_required,
    responseNotification,
    emails,
    isEmailInvalid,
    alreadyExistsEmail,
    submit_message,
    submitMessageLoading,
    savedData,
    formModal,
    isEdit,
    isDisabledFormField,
    informationModalDisplay,
    informationEditorContent,
    isSubmitLoading,
    isDisabled,
    switchBtnColor,
    inputRef,
    informationRef,
    labelNameRef,
    handleChange,
    handleOnChangeSelect,
    handleEmailDisplayed,
    handleEmailRequired,
    handleResponseNotification,
    handleInputChange,
    removeEmail,
    handleEditorInit,
    handleFileChange,
    handleDragEndDataForSavedData,
    handleChildFormModal,
    handleFormFieldUpdate,
    handleFormFieldDelete,
    handleFormModal,
    handleSubmitForm,
    handleFormField,
    handleOnSubmitOptions,
    handleOnPressCancel,
    handleDragEndData,
    hideShowInformationModal,
    closeFormModal,
  };
}
